import React from "react";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { WhatsAppSimulator } from "./WhatsAppSimulator";
import { useLead } from "../../context/LeadContext";
import { ArrowRight, Star } from "lucide-react";

const lines = ["Turn WhatsApp chats", "into qualified", "pipeline,"];

const wrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const { openLeadForm } = useLead();

  return (
    <section className="relative overflow-hidden pb-16 pt-32 sm:pt-40">
      {/* soft background accents */}
      <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-brand-light/60 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-silver-100 blur-3xl" />

      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-12">
        {/* left */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-silver-300/70 bg-white px-3.5 py-1.5 text-xs font-semibold text-silver-700 shadow-sm"
          >
            <span className="flex h-1.5 w-1.5 rounded-full bg-brand" />
            AI Sales Agent for WhatsApp · Qualifies leads 24/7
          </motion.div>

          <motion.h1
            variants={wrap}
            initial="hidden"
            animate="show"
            className="font-display text-5xl font-black leading-[1.02] tracking-tighter text-charcoal sm:text-6xl lg:text-7xl"
          >
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span variants={line} className="block">
                  {l}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span variants={line} className="block">
                on <span className="text-brand">autopilot.</span>
              </motion.span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-7 max-w-xl text-base font-medium leading-relaxed text-silver-700 sm:text-lg"
          >
            Digimonk.AI drops an AI agent inside WhatsApp that greets every enquiry in
            seconds, asks the right questions, scores intent, and books the hot ones,
            so your team only ever talks to buyers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Button
              data-testid="hero-primary-cta"
              onClick={() => openLeadForm({ source: "hero" })}
              className="group h-13 rounded-xl bg-brand px-7 py-6 text-base font-bold text-white shadow-lg shadow-brand/20 hover:bg-brand-hover"
            >
              Unlock your personalized demo
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <a href="#simulator">
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                className="h-13 w-full rounded-xl border-silver-300 px-7 py-6 text-base font-bold text-charcoal hover:bg-silver-100 sm:w-auto"
              >
                Try the live simulator
              </Button>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25 }}
            className="mt-8 flex items-center gap-4 text-sm text-silver-500"
          >
            <div className="flex -space-x-2">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="h-8 w-8 rounded-full border-2 border-white bg-gradient-to-br from-silver-300 to-silver-500"
                />
              ))}
            </div>
            <span className="flex items-center gap-1 font-semibold text-charcoal">
              <Star className="h-4 w-4 fill-brand text-brand" /> 4.9/5 · trusted by 300+ growth teams
            </span>
          </motion.div>
        </div>

        {/* right: simulator */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center lg:col-span-5 lg:justify-end"
          id="simulator"
        >
          <div className="animate-float">
            <WhatsAppSimulator />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
